import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trophy, Camera, Vote, Brain, Users, ChevronRight } from "lucide-react";
import { Toolbar, Card } from "./AdminLayout";
import { adminPromotions, adminAudience, TYPE_META } from "../lib/promotions";
import PromoBuilder from "./PromoBuilder";
import PromoDetail from "./PromoDetail";

const ICON = { sweepstakes: Trophy, photo: Camera, ballot: Vote, quiz: Brain };
const TEMPLATES = [
  { type: "sweepstakes", desc: "Daily or one-time entry, random winner draw" },
  { type: "photo", desc: "Readers upload photos, the public votes" },
  { type: "ballot", desc: "Best Of categories with nominees and text keywords" },
  { type: "quiz", desc: "Personality or trivia quiz, results saved to each entrant" },
];

export default function PromotionsAdmin() {
  const [tab, setTab] = useState("campaigns");
  const [items, setItems] = useState([]);
  const [builder, setBuilder] = useState(null);
  const [picker, setPicker] = useState(false);
  const [openId, setOpenId] = useState(null);
  const load = () => adminPromotions().then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);

  if (openId) return <PromoDetail id={openId} onBack={() => { setOpenId(null); load(); }} onEdit={(c) => setBuilder(c)} builder={builder} closeBuilder={() => setBuilder(null)} />;

  return (
    <div>
      <div className="mb-4 flex gap-2">
        {[["campaigns", "Campaigns"], ["audience", "Audience"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`promo-tab-${k}`} className={`rounded-full px-4 py-1.5 text-sm font-semibold ${tab === k ? "bg-[var(--navy)] text-white" : "bg-white text-slate-600 ring-1 ring-slate-200"}`}>{l}</button>
        ))}
      </div>
      {tab === "audience" ? <Audience /> : (
        <>
          <Toolbar title="Promotions" subtitle="Contests, photo contests, Best Of ballots and quizzes that grow your reader list and earn sponsor revenue" onAdd={() => setPicker(true)} addLabel="New Campaign" />
          <div className="space-y-2">
            {items.map((c) => {
              const Icon = ICON[c.type] || Trophy;
              return (
                <button key={c.id} onClick={() => setOpenId(c.id)} data-testid={`promo-row-${c.id}`} className="flex w-full items-center gap-4 rounded-xl border border-slate-200 bg-white p-4 text-left hover:border-[var(--gold)]">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[var(--gold)]/15 text-[var(--gold)]"><Icon size={20} /></span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-semibold text-[var(--navy)]">{c.title}</p>
                    <p className="text-xs text-slate-400">{TYPE_META[c.type]?.label}{c.sponsor_name && ` · ${c.sponsor_name}`}{!c.active && " · Hidden"}</p>
                  </div>
                  <div className="text-right"><p className="text-lg font-bold text-[var(--navy)]">{c.entry_count}</p><p className="text-[10px] uppercase text-slate-400">entries</p></div>
                  <ChevronRight size={18} className="text-slate-300" />
                </button>
              );
            })}
          </div>
        </>
      )}
      {picker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setPicker(false)}>
          <div onClick={(e) => e.stopPropagation()} data-testid="promo-template-picker" className="w-full max-w-2xl rounded-2xl bg-white p-6">
            <h3 className="text-xl font-bold text-[var(--navy)]">Choose a campaign type</h3>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {TEMPLATES.map((t) => {
                const Icon = ICON[t.type];
                return (
                  <button key={t.type} onClick={() => { setPicker(false); setBuilder({ type: t.type }); }} data-testid={`promo-template-${t.type}`} className="rounded-xl border border-slate-200 p-4 text-left hover:border-[var(--gold)] hover:bg-amber-50/40">
                    <Icon size={22} className="text-[var(--gold)]" /><p className="mt-2 font-bold text-[var(--navy)]">{TYPE_META[t.type].label}</p><p className="text-xs text-slate-500">{t.desc}</p>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
      {builder && <PromoBuilder initial={builder} onClose={() => setBuilder(null)} onSaved={(c) => { setBuilder(null); load(); toast.success("Campaign saved."); setOpenId(c.id); }} />}
    </div>
  );
}

function Audience() {
  const [rows, setRows] = useState([]);
  useEffect(() => { adminAudience().then(setRows).catch(() => {}); }, []);
  return (
    <div data-testid="promo-audience">
      <Toolbar title="Audience" subtitle={`${rows.length} people have entered your campaigns · every entry, vote and quiz result builds their profile`} />
      <Card className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500"><tr><th className="px-4 py-3">Person</th><th className="px-4 py-3">ZIP</th><th className="px-4 py-3">Entries</th><th className="px-4 py-3">Campaigns & interests</th><th className="px-4 py-3">Status</th></tr></thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r) => (
              <tr key={r.email} data-testid={`audience-${r.email}`}>
                <td className="px-4 py-3"><p className="font-semibold text-[var(--navy)]">{r.name}</p><p className="text-xs text-slate-400">{r.email}{r.phone ? ` · ${r.phone}` : ""}</p></td>
                <td className="px-4 py-3 text-slate-500">{r.zip || "—"}</td>
                <td className="px-4 py-3">{r.entries}</td>
                <td className="px-4 py-3"><div className="flex flex-wrap gap-1">{r.campaigns.map((c) => <span key={c} className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">{c}</span>)}{r.quiz_results.map((q) => <span key={q} className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">{q}</span>)}</div></td>
                <td className="px-4 py-3 text-xs">{r.member ? <span className="font-semibold text-emerald-700">Loyalty member</span> : r.opt_in_news ? "Newsletter opt-in" : "—"}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-400">No entrants yet.</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
